import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Calendar } from "lucide-react";

export default function Hero({ settings }) {
    const headline = settings?.hero_headline || "Protection that moves with you.";
    const sub =
        settings?.hero_subheadline ||
        "Personalized life & health guidance — no pressure, no guesswork.";
    return (
        <section className="relative overflow-hidden" data-testid="hero">
            {/* ambient gradient orbs */}
            <div aria-hidden className="pointer-events-none absolute inset-0">
                <div className="absolute -top-40 -left-40 h-[460px] w-[460px] rounded-full bg-gradient-to-br from-mm-sky/25 via-mm-blue/10 to-transparent blur-3xl" />
                <div className="absolute -bottom-56 right-0 h-[520px] w-[520px] rounded-full bg-gradient-to-tr from-mm-navy/20 via-mm-blue/10 to-transparent blur-3xl" />
            </div>

            <div className="mm-container relative pt-10 pb-16 sm:pt-20 sm:pb-28">
                <div className="grid items-center gap-10 lg:grid-cols-[1.15fr_1fr] lg:gap-12">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="mm-chip">
                            <Sparkles size={13} className="text-mm-blue" aria-hidden />
                            Concierge insurance, AI-assisted
                        </div>
                        <h1 className="mm-display mt-5 text-[2.15rem] leading-[1.05] text-mm-navy sm:text-[3.4rem] lg:text-[4.1rem]">
                            {headline.split(" ").slice(0, -1).join(" ")}{" "}
                            <span className="mm-gradient-text">
                                {headline.split(" ").slice(-1)}
                            </span>
                        </h1>
                        <p className="mt-4 max-w-xl text-[0.95rem] leading-relaxed text-mm-muted sm:mt-5 sm:text-lg">
                            {sub} Licensed advisors. Independent guidance across eight
                            lines of coverage. A modern way to find what fits.
                        </p>

                        <div className="mt-7 flex flex-wrap gap-3 sm:mt-8">
                            <Link to="/#lead-form" className="mm-btn-primary" data-testid="hero-cta-quote">
                                Get My Free Quote
                                <ArrowRight size={16} />
                            </Link>
                            <Link to="/jarvis" className="mm-btn-outline" data-testid="hero-cta-jarvis">
                                <Sparkles size={15} />
                                Start With Jarvis
                            </Link>
                            <Link to="/book" className="mm-btn-ghost" data-testid="hero-cta-book">
                                <Calendar size={15} />
                                Book a Call
                            </Link>
                        </div>

                        <div className="mt-8 flex flex-wrap items-center gap-2 text-xs text-mm-muted sm:mt-10 sm:gap-5">
                            <span className="mm-chip">Licensed in your state</span>
                            <span className="mm-chip">No-pressure guidance</span>
                            <span className="mm-chip">We don't sell your data</span>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="relative mx-auto w-full max-w-xs sm:max-w-sm"
                    >
                        <div className="relative">
                            <div className="absolute -inset-3 rounded-[28px] bg-gradient-to-br from-mm-navy via-mm-blue to-mm-sky opacity-90 blur-[1px]" />
                            <div className="relative overflow-hidden rounded-[24px] border border-white/30 bg-white shadow-hover">
                                <img
                                    src="/brand/brent.jpg"
                                    alt="Brent Deparis, President of Moving Mountains Advisors"
                                    className="h-[240px] w-full object-cover object-[center_top] sm:h-[360px] lg:h-[420px]"
                                    loading="eager"
                                    fetchpriority="high"
                                    data-testid="hero-portrait"
                                />
                                <div className="absolute bottom-3 left-3 right-3 rounded-2xl bg-white/95 px-4 py-2.5 backdrop-blur">
                                    <div className="text-[0.68rem] uppercase tracking-[0.2em] text-mm-muted">
                                        President
                                    </div>
                                    <div className="mm-display text-base font-semibold text-mm-navy">
                                        Brent Deparis
                                    </div>
                                    <div className="text-[11px] text-mm-muted">
                                        Moving Mountains Advisors
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
